import { Button } from '@/components/Button'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import { useId, type ReactNode } from 'react'
import type { BookkeepingOtherConnectionDialogViewModel } from './BookkeepingOtherConnectionDialog'
import { Form } from '@/components/Form'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import {
  availableBookkeepingSoftwareTypes,
  formatAvailableBookkeepingSoftware,
} from '@/features/bookkeeping/platform-connections/available-bookkeeping-software'

interface BookkeepingOtherConnectionDialogContainerProps {
  vm: BookkeepingOtherConnectionDialogViewModel
}

/**
 * The BookkeepingOtherConnectionDialogContainer
 */
export const BookkeepingOtherConnectionDialogContainer = observer(
  ({ vm }: BookkeepingOtherConnectionDialogContainerProps) => {
    return (
      <BookkeepingOtherConnectionDialog
        isOpen={vm.modal.showing}
        onClose={vm.modal.close}
        isPending={vm.save.pending}
        onSubmit={vm.save}
      >
        <SelectContainer
          label="Bookkeeping software"
          field="bookkeepingSoftware"
          placeholder="Select software"
          error={vm.validation.getError('bookkeepingSoftware')}
          model={vm}
          items={availableBookkeepingSoftwareTypes.map((type) => {
            return [type, formatAvailableBookkeepingSoftware(type)]
          })}
          getKey={([code]) => code}
          getLabel={([_, label]) => label}
        />
      </BookkeepingOtherConnectionDialog>
    )
  },
)

interface BookkeepingOtherConnectionDialogProps {
  children?: ReactNode
  isOpen: boolean
  isPending?: boolean
  isDisabled?: boolean
  onClose?: () => unknown
  onSubmit?: () => unknown
}

/**
 * The BookkeepingOtherConnectionDialog
 */
export function BookkeepingOtherConnectionDialog({
  children,
  isOpen,
  isPending,
  onClose,
  onSubmit,
}: BookkeepingOtherConnectionDialogProps) {
  const formId = useId()
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Select your bookkeeping software</ModalDialogTitle>
        <ModalDialogDescription>
          Select the software you currently use. An admin will contact you with
          instructions on how to give your Pro access to your books.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <Form onSubmit={onSubmit} id={formId}>
          {children}
        </Form>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Save & continue"
            type="submit"
            isPending={isPending}
            form={formId}
          />
          <Button
            label="Cancel"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
